import { connect } from 'react-redux';
import { compose } from 'redux';
import { createSelector, createStructuredSelector } from 'reselect';
import qs from 'querystring';
import { get } from 'lodash';
import moment from 'moment';
import config from 'config';
import PLAYER_TYPES from 'constants/player-types';
import { AVERAGE_LIVE_LATENCY } from 'config';
import {
  onPlayerOpen,
  onPlayerClose,
  onStartPlaying,
  onStopPlaying,
  onEnded,
  onError,
  onTimeUpdate,
  toggleInFavoriteStatus,
  loadPlayerData,
  navigateToFirstAvailableChannel,
  navigateToPlayerPage,
  changeProgram,
} from 'actions/player';
import { showOverlay, hideOverlay } from 'actions/overlay';
import { showEPG, hideEPG } from 'actions/epg-widget';
import { setMenuIdle } from 'actions/menu';
import { getVerimatrixIdSelector } from 'reducers/profile/selectors';
import { getErrorSelector, getIsPlayedDataLoaded } from 'reducers/player/selectors';
import {
  getCurrentChannelByPropsSelector,
  getCurrentProgramByPropsSelector,
  getChannelNavigationSelector,
} from 'reducers/channels/selectors';
import { getCurrentVideoObjectDataByPropsSelector, getDetailsNavigationSelector } from 'reducers/details/selectors';
import { selectIsVisible } from 'reducers/epg-widget/selectors';
import withBackSupport from 'components/HOCs/withBackSupport';
import withScrollHack from 'components/HOCs/withScrollHack';

import Page from './Page';
import Platform from 'platform';
import runtimeToSeconds from 'utils/runtime-to-seconds';

const getAuthStatusSelector = (state) => state.profile.isLoggedIn;
const getPathSelector = (_state, props) => props.history.location.pathname;
const getVideoIdSelector = (_state, props) => parseInt(props.match.params.videoId, 10) || null;
const getSeriesIdSelector = (_state, props) => parseInt(props.match.params.seriesId, 10) || null;
const getEpisodeIdSelector = (_state, props) => parseInt(props.match.params.episodeId, 10) || null;
const getChannelIdSelector = (_state, props) => parseInt(props.match.params.channelId, 10) || null;
const getStartTimeSelector = (_state, props) => props.match.params.startTime || null;
const getTimeshiftBeginSelector = (_state, props) => {
  return new URLSearchParams(props.history.location.search).get('timeshiftBegin');
};

const getQueryParamsSelector = createSelector(
  getVideoIdSelector,
  getSeriesIdSelector,
  getEpisodeIdSelector,
  getChannelIdSelector,
  getStartTimeSelector,
  (videoId, seriesId, episodeId, channelId, startTime) => ({
    videoId,
    seriesId,
    episodeId,
    channelId,
    startTime,
  }),
);

const getPlayerTypeSelector = createSelector(
  getPathSelector,
  getQueryParamsSelector,
  getTimeshiftBeginSelector,
  (pathname, { startTime }, timeshiftBegin) => {
    if (/\/vod\//.test(pathname)) {
      return PLAYER_TYPES.VOD;
    } else if (/\/series/.test(pathname)) {
      return PLAYER_TYPES.SERIES;
    } else if (/\/vod-channel/.test(pathname)) {
      return PLAYER_TYPES.VOD_CHANNEL;
    } else if (/\/channel/.test(pathname)) {
      if (startTime) return PLAYER_TYPES.CATCHUP;
      if (timeshiftBegin && (Platform.getModelYear() >= 2018 || Platform.getName() === 'Hisense')) {
        return PLAYER_TYPES.TIMESHIFT;
      }
      return PLAYER_TYPES.CHANNEL;
    } else if (/\/trailer\//.test(pathname)) {
      return PLAYER_TYPES.TRAILER;
    }
  },
);

const getContentSelector = createSelector(
  getPlayerTypeSelector,
  getCurrentVideoObjectDataByPropsSelector,
  getCurrentChannelByPropsSelector,
  getCurrentProgramByPropsSelector,
  (playerType, videoObj, channel, program) => {
    if ([PLAYER_TYPES.VOD, PLAYER_TYPES.SERIES, PLAYER_TYPES.TRAILER].includes(playerType)) {
      return videoObj;
    }
    if ([PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)) return channel;
    if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) return program;
  },
);

const selectIsNavigationDisabled = createSelector(
  (_, props) => get(props.history, ['location', 'search'], ''),
  (search) => {
    const { navigation } = qs.parse(decodeURIComponent(search).replace('?', ''));
    return navigation === 'false';
  },
);

const getNavigationSelector = createSelector(
  selectIsNavigationDisabled,
  getPlayerTypeSelector,
  getChannelNavigationSelector,
  getDetailsNavigationSelector,
  (isDisabled, playerType, channel, details) => {
    if (isDisabled) return null;
    if (
      [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.VOD_CHANNEL, PLAYER_TYPES.CATCHUP, PLAYER_TYPES.TIMESHIFT].includes(
        playerType,
      )
    ) {
      return channel;
    }
    if (playerType === PLAYER_TYPES.SERIES) return details;
  },
);

const getStartFromSelector = createSelector(
  getPlayerTypeSelector,
  getContentSelector,
  getTimeshiftBeginSelector,
  (playerType, content, timeshiftBegin) => {
    if (playerType === PLAYER_TYPES.TRAILER) return 0;
    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      return moment(Date.now()).subtract(AVERAGE_LIVE_LATENCY, 's').diff(timeshiftBegin, 's');
    }

    if (playerType === PLAYER_TYPES.VOD_CHANNEL && content) {
      const now = moment().add(config.timeDiffFromServer, 's');
      const isLiveProgram = moment(content.startTime) < now && moment(content.endTime) > now;
      const time = (now - moment(content.startTime)) / 1000;

      return isLiveProgram ? time : 0;
    }

    // FIX for Samsung QA round "Video starts from finish time and VOD player immediately closes."
    if (playerType === PLAYER_TYPES.VOD && content) {
      const duration = runtimeToSeconds(content.title?.runTime) || 0;
      const bookmark = get(content, ['bookmark', 'seconds'], 0);

      return bookmark >= duration ? 0 : bookmark;
    }

    return get(content, ['bookmark', 'seconds'], 0);
  },
);

const getVideoSrcSelector = createSelector(
  getPlayerTypeSelector,
  getContentSelector,
  getTimeshiftBeginSelector,
  (playerType, content) => {
    if (!content) return '';
    let url = '';
    if ([PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)) url = content.contentUrls.dash;

    if (playerType === PLAYER_TYPES.VOD) url = content.movie.contentUrl;
    if (playerType === PLAYER_TYPES.TRAILER) {
      url = content.previewContentUrls.trailer;
      url = url.replace('http://', 'https://');
    }
    if (playerType === PLAYER_TYPES.SERIES) url = content.movie.contentUrl;
    if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) {
      url = content.cuTvUrl;
    }

    return url || '';
  },
);

const isHLSPlayerRequiredSelector = createSelector(getContentSelector, getPlayerTypeSelector, (content, playerType) => {
  // TODO: sometimes it returns "false" fro TRAILER player because content isn't loaded yet
  // Maybe fix it
  if (!content) return false;
  return content.assetId?.includes('MEGO') || playerType === PLAYER_TYPES.TRAILER;
});

const isChannelChangeAllowedSelector = createSelector(getPlayerTypeSelector, (playerType) =>
  [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.VOD_CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType),
);

const isFVODContentSelector = createSelector(
  getContentSelector,
  (content) => get(content, ['visibilityVodType'], '') === 'FVOD',
);

const selectIsDeepLink = createSelector(
  (_, props) => get(props.history, ['location', 'search'], ''),
  (search) => {
    const { isDeepLink } = qs.parse(decodeURIComponent(search).replace('?', ''));
    return isDeepLink === 'true';
  },
);

const mapStateToProps = createStructuredSelector({
  playerType: getPlayerTypeSelector,
  content: getContentSelector,
  navigation: getNavigationSelector,
  startFrom: getStartFromSelector,
  timeshiftBegin: getTimeshiftBeginSelector,
  src: getVideoSrcSelector,
  verimatrixId: getVerimatrixIdSelector,
  queryParams: getQueryParamsSelector,
  isChannelChangeAllowed: isChannelChangeAllowedSelector,
  isHLSPlayerRequired: isHLSPlayerRequiredSelector,
  isFVODContent: isFVODContentSelector,
  error: getErrorSelector,
  isLoggedIn: getAuthStatusSelector,
  isEPGOpen: selectIsVisible,
  isPlayedDataLoaded: getIsPlayedDataLoaded,
  isDeepLink: selectIsDeepLink,
});

const mapDispatchToProps = {
  loadPlayerData,
  navigateToFirstAvailableChannel,
  navigateToPlayerPage,
  showOverlay,
  hideOverlay,
  onStartPlaying,
  onStopPlaying,
  onPlayerOpen,
  onPlayerClose,
  onTimeUpdate,
  onEnded,
  onError,
  toggleInFavoriteStatus,
  changeProgram,
  showEPG,
  hideEPG,
  setMenuIdle,
};

export default compose(withBackSupport, withScrollHack, connect(mapStateToProps, mapDispatchToProps))(Page);
